import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Skeleton,
  Divider,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ShowChart as ChartIcon,
  Close as CloseIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Balance as BalanceIcon,
  WarningAmber as WarningIcon,
  Shield as ShieldIcon,
  Lightbulb as LightbulbIcon,
  EmojiEvents as TrophyIcon,
  Assessment as AssessmentIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import Snackbar from '@mui/material/Snackbar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { trendPredictionsAPI } from '../services/api';

/* ── helpers ── */
const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};
const fmtDate = (s) => {
  if (!s || s === '—') return '—';
  try { const d = new Date(s); return d.toLocaleDateString('vi-VN', { day:'2-digit', month:'2-digit', year:'numeric' }); }
  catch { return s; }
};

/* ── verdict / regime config ── */
const V = {
  STRONG_CONTINUE: { c: '#1b5e20', bg: '#e8f5e9', dot: '#4caf50' },
  CONTINUE:        { c: '#e65100', bg: '#fff3e0', dot: '#ff9800' },
  WEAKENING:       { c: '#bf360c', bg: '#fbe9e7', dot: '#ff5722' },
  REVERSAL:        { c: '#424242', bg: '#f5f5f5', dot: '#9e9e9e' },
  BEAR_MARKET:     { c: '#616161', bg: '#eee',    dot: '#757575' },
};
const R = {
  BULL:    { c: '#2e7d32', bg: '#e8f5e9', Icon: TrendingUpIcon },
  NEUTRAL: { c: '#ed6c02', bg: '#fff8e1', Icon: BalanceIcon },
  BEAR:    { c: '#c62828', bg: '#ffebee', Icon: TrendingDownIcon },
};

/* ────────────────────────────────────────────────────
   Score bar – gradient, responsive
   ──────────────────────────────────────────────────── */
const ScoreBar = ({ score, wide }) => {
  const pct = Math.min(score, 100);
  const c = score >= 75 ? '#2e7d32' : score >= 55 ? '#f57c00' : score >= 40 ? '#e65100' : '#c62828';
  return (
    <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 800, color: c, minWidth: 28, fontSize: '0.85rem', fontFeatureSettings: '"tnum"' }}>
        {score.toFixed(0)}
      </Typography>
      <Box sx={{ flex: 1, maxWidth: wide ? 100 : 80, height: 6, borderRadius: 3, bgcolor: '#f0f0f0', overflow:'hidden' }}>
        <Box sx={{ width:`${pct}%`, height:'100%', borderRadius: 3,
          background:`linear-gradient(90deg, ${rgba(c,.4)}, ${c})`, transition:'width .4s ease' }} />
      </Box>
    </Box>
  );
};

/* ────────────────────────────────────────────────────
   Distribution histogram – FULL WIDTH, tall bars
   ──────────────────────────────────────────────────── */
const DistributionChart = ({ histogram, total }) => {
  const { t } = useTranslation();
  if (!histogram?.length) return null;
  const max = Math.max(...histogram.map(h => h.count));
  return (
    <Box sx={{ display:'flex', alignItems:'flex-end', gap:'2px', height: 70, width:'100%' }}>
      {histogram.map((h, i) => {
        const pct = max > 0 ? (h.count / max) * 100 : 0;
        const n = parseInt(h.bin);
        const c = n >= 75 ? '#4caf50' : n >= 55 ? '#ff9800' : n >= 40 ? '#ff5722' : '#9e9e9e';
        const ratio = total > 0 ? ((h.count / total) * 100).toFixed(0) : 0;
        const showLabel = pct >= 35 && h.count > 0;
        return (
          <Tooltip key={i} title={t('trendPredictions.distribution.binTooltip', { bin: h.bin, count: h.count, pct: ratio })} arrow>
            <Box sx={{
              flex: 1, height:`${Math.max(pct, 4)}%`, minHeight: 3, position:'relative',
              bgcolor: rgba(c, .55), borderRadius:'3px 3px 0 0', cursor:'pointer',
              transition:'all .25s',
              '&:hover':{ bgcolor: c, transform:'scaleY(1.06)', boxShadow:`0 -2px 8px ${rgba(c,.25)}` },
            }}>
              {showLabel && (
                <Typography sx={{ position:'absolute', top:2, left:0, right:0, textAlign:'center',
                  fontSize:'0.55rem', fontWeight:700, color:'rgba(255,255,255,.85)', lineHeight:1 }}>
                  {h.count}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

/* ────────────────────────────────────────────────────
   Proportion bar – stacked verdict ratios
   ──────────────────────────────────────────────────── */
const ProportionBar = ({ counts, total, activeFilter, onFilter }) => {
  const { t } = useTranslation();
  if (!total) return null;
  const segs = [
    { key:'STRONG_CONTINUE', c:'#4caf50' },
    { key:'CONTINUE',        c:'#ff9800' },
    { key:'WEAKENING',       c:'#ff5722' },
    { key:'REVERSAL',        c:'#9e9e9e' },
  ];
  return (
    <Box sx={{ display:'flex', height: 24, borderRadius: 1.5, overflow:'hidden', cursor:'pointer', border:'1px solid rgba(0,0,0,.06)' }}>
      {segs.map(s => {
        const n = counts[s.key] || 0;
        const pct = (n / total * 100);
        if (pct < 1) return null;
        const active = activeFilter === s.key;
        return (
          <Tooltip key={s.key} title={t('trendPredictions.distribution.stockTooltip', { label: t(`trendPredictions.verdictShort.${s.key}`), count: n, pct: pct.toFixed(0) })} arrow>
            <Box onClick={() => onFilter(active ? 'ALL' : s.key)}
              sx={{
                width:`${pct}%`, bgcolor: active ? s.c : rgba(s.c, .5),
                display:'flex', alignItems:'center', justifyContent:'center', gap:.3,
                transition:'all .2s', position:'relative',
                '&:hover':{ bgcolor: s.c, filter:'brightness(1.05)' },
                borderRight: '1px solid rgba(255,255,255,.4)',
              }}>
              {pct >= 12 && (
                <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:'#fff', textShadow:'0 1px 2px rgba(0,0,0,.25)', whiteSpace:'nowrap' }}>
                  {t(`trendPredictions.verdictShort.${s.key}`)} {n}
                </Typography>
              )}
              {pct >= 6 && pct < 12 && (
                <Typography sx={{ fontSize:'0.58rem', fontWeight:700, color:'#fff', textShadow:'0 1px 2px rgba(0,0,0,.25)' }}>
                  {n}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

/* ────────────────────────────────────────────────────
   Symbol History Dialog
   ──────────────────────────────────────────────────── */
const HistoryDialog = ({ symbol, open, onClose }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !symbol) return;
    setLoading(true);
    trendPredictionsAPI.getSymbol(symbol, 20)
      .then(r => {
        const raw = r.data?.predictions || [];
        // compute score delta between consecutive days
        for (let i = 0; i < raw.length - 1; i++) {
          raw[i]._delta = +(raw[i].composite_score - raw[i+1].composite_score).toFixed(1);
        }
        setData(raw);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [symbol, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx:{ borderRadius: 3 } }}>
      <DialogTitle sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', pb:1 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <TimelineIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">{symbol}</Typography>
          <Typography variant="body2" color="textSecondary">{t('trendPredictions.history.scoreHistory')}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {loading ? (
          <Box sx={{ py:4, textAlign:'center' }}><CircularProgress /></Box>
        ) : data.length === 0 ? (
          <Alert severity="info">{t('trendPredictions.history.noData')}</Alert>
        ) : (
          <>
            {/* spark bars */}
            <Box sx={{ display:'flex', alignItems:'flex-end', gap:'3px', height:56, mb:2 }}>
              {[...data].reverse().map((h,i) => {
                const pct = Math.min(h.composite_score, 100);
                const c = pct >= 75 ? '#4caf50' : pct >= 55 ? '#ff9800' : pct >= 40 ? '#ff5722' : '#9e9e9e';
                return (
                  <Tooltip key={i} title={`${fmtDate(h.date)}: ${pct.toFixed(0)}`} arrow>
                    <Box sx={{ flex:1, height:`${pct}%`, bgcolor:rgba(c,.65), borderRadius:'3px 3px 0 0', minWidth:6 }} />
                  </Tooltip>
                );
              })}
            </Box>
            <Divider sx={{ mb:1 }} />
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['date','score','delta','pCont','rank','signal'].map(h => (
                    <TableCell key={h} sx={{ fontWeight:600, fontSize:'0.72rem', color:'#666', py:.5 }}>{t(`trendPredictions.history.${h}`)}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((h,i) => {
                  const cfg = V[h.verdict] || V.REVERSAL;
                  const verdictKey = V[h.verdict] ? h.verdict : 'REVERSAL';
                  return (
                    <TableRow key={i} sx={{ '&:last-child td':{ border:0 } }}>
                      <TableCell sx={{ fontSize:'0.78rem', py:.4 }}>{fmtDate(h.date)}</TableCell>
                      <TableCell sx={{ py:.4 }}><ScoreBar score={h.composite_score} /></TableCell>
                      <TableCell sx={{ py:.4 }}>
                        {h._delta != null ? (
                          <Typography sx={{ fontSize:'0.72rem', fontWeight:700,
                            color: h._delta > 0 ? '#2e7d32' : h._delta < 0 ? '#c62828' : '#999' }}>
                            {h._delta > 0 ? '+' : ''}{h._delta}
                          </Typography>
                        ) : <Typography sx={{ fontSize:'0.72rem', color:'#ccc' }}>—</Typography>}
                      </TableCell>
                      <TableCell sx={{ fontSize:'0.78rem', py:.4 }}>{(h.weighted_p*100).toFixed(1)}%</TableCell>
                      <TableCell sx={{ fontSize:'0.78rem', py:.4 }}>#{h.rank}</TableCell>
                      <TableCell sx={{ py:.4 }}>
                        <Chip label={t(`trendPredictions.verdict.${verdictKey}`)} size="small"
                          sx={{ bgcolor:cfg.bg, color:cfg.c, fontWeight:600, fontSize:'0.62rem', height:20 }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ══════════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════════ */
const TrendPredictions = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading]       = useState(true);
  const [predictions, setPred]      = useState([]);
  const [regime, setRegime]         = useState(null);
  const [distribution, setDist]     = useState(null);
  const [error, setError]           = useState(null);
  const [search, setSearch]         = useState('');
  const [vFilter, setVFilter]       = useState('ALL');
  const [orderBy, setOrderBy]       = useState('composite_score');
  const [orderDir, setOrderDir]     = useState('desc');
  const [page, setPage]             = useState(0);
  const [rowsPerPage, setRPP]       = useState(25);
  const [histSym, setHistSym]       = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [accuracy, setAccuracy]     = useState(null);
  const [dailyAcc, setDailyAcc]     = useState([]);
  const [stockAcc, setStockAcc]     = useState([]);
  const [accError, setAccError]     = useState(false);
  const [showAccuracy, setShowAccuracy] = useState(true);
  const [returnProfile, setReturnProfile] = useState(null);
  const [showReturnProfile, setShowReturnProfile] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  /* ── fetch ── */
  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const [pR, rR, dR] = await Promise.allSettled([
        trendPredictionsAPI.getPredictions({ topN: 500, minScore: 0 }),
        trendPredictionsAPI.getRegime(),
        trendPredictionsAPI.getDistribution(),
      ]);
      if (pR.status === 'fulfilled') setPred(pR.value?.data || []);
      else setError(t('trendPredictions.errors.loadFailed'));
      if (rR.status === 'fulfilled') setRegime(rR.value?.data || null);
      if (dR.status === 'fulfilled') setDist(dR.value?.data || null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); setRefreshing(false); }
  }, [t]);

  /* ── recalculate on-demand ── */
  const handleRecalculate = useCallback(async () => {
    setCalculating(true);
    try {
      const res = await trendPredictionsAPI.calculate();
      const d = res?.data;
      setSnackbar({
        open: true, severity: 'success',
        message: t('trendPredictions.recalculate.success', {
          total: d?.total || 0, strong: d?.strong_continue || 0, elapsed: d?.elapsed_seconds?.toFixed(1) || '?',
        }),
      });
      await fetchData(true);
    } catch (e) {
      setSnackbar({ open: true, severity: 'error', message: t('trendPredictions.recalculate.error') });
    } finally { setCalculating(false); }
  }, [t, fetchData]);

  /* ── fetch accuracy ── */
  const fetchAccuracy = useCallback(async () => {
    try {
      setAccError(false);
      const [sR, dR, stR, rpR] = await Promise.allSettled([
        trendPredictionsAPI.getAccuracySummary(30),
        trendPredictionsAPI.getDailyAccuracy(60),
        trendPredictionsAPI.getStockAccuracy(30, 15),
        trendPredictionsAPI.getReturnProfile(60),
      ]);
      if (sR.status === 'fulfilled') setAccuracy(sR.value?.data || null);
      if (dR.status === 'fulfilled') setDailyAcc(dR.value?.data || []);
      if (stR.status === 'fulfilled') setStockAcc(stR.value?.data || []);
      if (rpR.status === 'fulfilled') setReturnProfile(rpR.value?.data || null);
      if (sR.status === 'rejected' && dR.status === 'rejected') setAccError(true);
    } catch (e) { setAccError(true); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchAccuracy(); }, [fetchAccuracy]);

  /* ── sort ── */
  const handleSort = (col) => {
    if (orderBy === col) setOrderDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(col); setOrderDir(col === 'symbol' ? 'asc' : 'desc'); }
  };

  /* ── derived ── */
  const counts = useMemo(() => {
    const c = { STRONG_CONTINUE:0, CONTINUE:0, WEAKENING:0, REVERSAL:0 };
    predictions.forEach(p => {
      if (p.verdict === 'BEAR_MARKET') c.REVERSAL += 1;
      else c[p.verdict] = (c[p.verdict]||0)+1;
    });
    return c;
  }, [predictions]);

  const filtered = useMemo(() => {
    let d = [...predictions];
    if (search) { const s = search.toUpperCase(); d = d.filter(p => p.symbol.includes(s)); }
    if (vFilter !== 'ALL') d = d.filter(p => p.verdict === vFilter);
    d.sort((a,b) => {
      let va = a[orderBy], vb = b[orderBy];
      if (typeof va === 'string') { va = va.toUpperCase(); vb = vb.toUpperCase(); }
      return orderDir === 'asc' ? (va<vb?-1:va>vb?1:0) : (va>vb?-1:va<vb?1:0);
    });
    return d;
  }, [predictions, search, vFilter, orderBy, orderDir]);

  const pageData = useMemo(() => filtered.slice(page*rowsPerPage, (page+1)*rowsPerPage), [filtered, page, rowsPerPage]);
  const predDate = predictions[0]?.date || distribution?.date || '—';
  const rCfg = R[regime?.regime] || R.NEUTRAL;

  /* ── loading ── */
  if (loading) return (
    <Box sx={{ px: { xs:1.5, sm:2.5, md:3 }, py: 2, width:'100%', maxWidth:'100%', boxSizing:'border-box' }}>

      {/* Header skeleton */}
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
          <Skeleton variant="rounded" width={42} height={42} sx={{ borderRadius:2.5, flexShrink:0 }} />
          <Box>
            <Skeleton variant="text" width={220} height={28} />
            <Skeleton variant="text" width={280} height={16} sx={{ mt:.3 }} />
          </Box>
        </Box>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <Skeleton variant="rounded" width={8} height={8} sx={{ borderRadius:'50%' }} />
          <Skeleton variant="text" width={55} height={16} />
          <Skeleton variant="text" width={70} height={16} />
          <Skeleton variant="text" width={80} height={16} />
          <Skeleton variant="text" width={65} height={16} />
          <Skeleton variant="circular" width={28} height={28} />
        </Box>
      </Box>

      {/* 5-card metrics grid */}
      <Box sx={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:1.5, mb:2 }}>
        {[1,2,3,4,5].map(i => (
          <Paper key={i} elevation={0} sx={{ borderRadius:2, overflow:'hidden', border:'1px solid rgba(0,0,0,.06)' }}>
            <Skeleton variant="rectangular" height={2.5} />
            <Box sx={{ px:1.3, pt:.8, pb:1 }}>
              <Skeleton variant="text" width={50} height={12} sx={{ mb:.4 }} />
              <Skeleton variant="text" width={40} height={32} />
              <Skeleton variant="text" width={70} height={12} sx={{ mt:.4 }} />
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Distribution + Proportion bar */}
      <Paper elevation={0} sx={{ p:1.5, mb:2, border:'1px solid #e8e8e8', borderRadius:2 }}>
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:.8 }}>
          <Skeleton variant="text" width={120} height={16} />
          <Skeleton variant="text" width={180} height={14} />
        </Box>
        {/* Histogram bars */}
        <Box sx={{ display:'flex', alignItems:'flex-end', gap:'2px', height:70, mb:.5 }}>
          {Array.from({ length: 20 }, (_, i) => (
            <Skeleton key={i} variant="rectangular" sx={{ flex:1, borderRadius:'3px 3px 0 0',
              height: `${15 + Math.sin(i * 0.5) * 30 + Math.random() * 40}%` }} />
          ))}
        </Box>
        {/* Scale labels */}
        <Box sx={{ display:'flex', justifyContent:'space-between', mt:.3, mb:1 }}>
          {[1,2,3,4,5,6].map(i => <Skeleton key={i} variant="text" width={16} height={12} />)}
        </Box>
        {/* Proportion bar */}
        <Skeleton variant="rounded" height={24} sx={{ borderRadius:1.5 }} />
      </Paper>

      {/* Filter bar */}
      <Box sx={{ display:'flex', gap:1.5, mb:1.5, alignItems:'center' }}>
        <Skeleton variant="rounded" width={160} height={34} sx={{ borderRadius:2 }} />
        {[80, 60, 65, 60, 65].map((w, i) => (
          <Skeleton key={i} variant="rounded" width={w} height={28} sx={{ borderRadius:14 }} />
        ))}
        <Box sx={{ flex:1 }} />
        <Skeleton variant="text" width={70} height={16} />
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ border:'1px solid #e8e8e8', borderRadius:2, overflow:'hidden' }}>
        {/* Header row */}
        <Box sx={{ display:'flex', alignItems:'center', px:2, py:1, bgcolor:'#fafafa', borderBottom:'2px solid #eee' }}>
          {[{ w:'4%' }, { w:'10%' }, { w:'16%' }, { w:'7%' }, { w:'10%' }, { w:'12%' }, { w:'15%' }].map((col, i) => (
            <Box key={i} sx={{ width: col.w, px:.5 }}>
              <Skeleton variant="text" width="70%" height={14} />
            </Box>
          ))}
        </Box>
        {/* Data rows */}
        {Array.from({ length: 10 }, (_, i) => (
          <Box key={i} sx={{ display:'flex', alignItems:'center', px:2, py:1,
            borderBottom:'1px solid #f5f5f5', bgcolor: i % 2 === 0 ? 'rgba(0,0,0,.008)' : 'transparent' }}>
            <Box sx={{ width:'4%', px:.5 }}>
              <Skeleton variant="text" width={18} height={18} />
            </Box>
            <Box sx={{ width:'10%', px:.5 }}>
              <Skeleton variant="text" width={48} height={18} sx={{ fontWeight:700 }} />
            </Box>
            <Box sx={{ width:'16%', px:.5, display:'flex', alignItems:'center', gap:1 }}>
              <Skeleton variant="text" width={24} height={16} />
              <Skeleton variant="rounded" width={60} height={6} sx={{ borderRadius:3 }} />
            </Box>
            <Box sx={{ width:'7%', px:.5 }}>
              <Skeleton variant="text" width={30} height={16} />
            </Box>
            <Box sx={{ width:'10%', px:.5 }}>
              <Skeleton variant="text" width={40} height={16} />
            </Box>
            <Box sx={{ width:'12%', px:.5 }}>
              <Skeleton variant="rounded" width={65} height={22} sx={{ borderRadius:11 }} />
            </Box>
            <Box sx={{ width:'15%', px:.5 }}>
              <Skeleton variant="text" width={45} height={20} />
              <Skeleton variant="rounded" width={50} height={4} sx={{ borderRadius:2, mt:.3 }} />
            </Box>
          </Box>
        ))}
        {/* Pagination */}
        <Box sx={{ display:'flex', justifyContent:'flex-end', alignItems:'center', px:2, py:.8, borderTop:'1px solid #f0f0f0', gap:2 }}>
          <Skeleton variant="text" width={60} height={14} />
          <Skeleton variant="rounded" width={45} height={24} sx={{ borderRadius:1 }} />
          <Skeleton variant="text" width={70} height={14} />
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="circular" width={24} height={24} />
        </Box>
      </Paper>
    </Box>
  );

  return (
    <Box sx={{ px: { xs:1.5, sm:2.5, md:3 }, py: 2, width:'100%', maxWidth:'100%', boxSizing:'border-box' }}>

      {/* ═══════════════ HEADER ═══════════════ */}
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2 }}>
        {/* left: icon + title */}
        <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
          <Box sx={{
            width:42, height:42, borderRadius:2.5, flexShrink:0,
            background:'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
            display:'flex', alignItems:'center', justifyContent:'center',
            boxShadow:`0 3px 10px ${rgba('#1565c0', .3)}`,
          }}>
            <ChartIcon sx={{ fontSize:22, color:'#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:'1.35rem', fontWeight:800, color:'#1e293b', lineHeight:1.2 }}>
              {t('trendPredictions.title')}
            </Typography>
            <Typography sx={{ fontSize:'0.8rem', color:'#64748b', fontWeight:500, lineHeight:1.3, mt:0.3 }}>
              {t('trendPredictions.subtitle', { date: fmtDate(predDate) })}
            </Typography>
          </Box>
        </Box>

        {/* right: regime status — clean inline style */}
        <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap:'wrap', justifyContent:'flex-end' }}>
          {/* Regime dot + label */}
          <Box sx={{ display:'flex', alignItems:'center', gap:.5, px:1 }}>
            <Box sx={{ width:7, height:7, borderRadius:'50%', bgcolor: rCfg.c,
              boxShadow:`0 0 0 2px ${rgba(rCfg.c,.15)}, 0 0 6px ${rgba(rCfg.c,.25)}`,
              animation: regime?.regime === 'BULL' ? 'pulse 2s ease-in-out infinite' : 'none',
              '@keyframes pulse':{ '0%,100%':{ opacity:1 }, '50%':{ opacity:.6 } },
            }} />
            <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color: rCfg.c, letterSpacing:.3 }}>
              {t(`trendPredictions.regime.${regime?.regime || 'NEUTRAL'}`)}
            </Typography>
          </Box>
          {regime && (
            <>
              <Typography sx={{ color:'#e0e0e0', fontSize:'0.9rem', mx:.3 }}>·</Typography>
              {/* Trend arrow */}
              <Typography sx={{ fontSize:'0.75rem', fontWeight:600,
                color: regime.m_trend_up === 1 ? '#2e7d32' : '#c62828' }}>
                {regime.m_trend_up === 1 ? '↑' : '↓'} {t('trendPredictions.regime.trend')}
              </Typography>
              <Typography sx={{ color:'#e0e0e0', fontSize:'0.9rem', mx:.3 }}>·</Typography>
              {/* VNI return */}
              <Typography sx={{ fontWeight:700, fontSize:'0.78rem',
                color: (regime.m_ret_10 || 0) >= 0 ? '#2e7d32' : '#c62828' }}>
                VNI {(regime.m_ret_10 || 0) > 0 ? '+' : ''}{((regime.m_ret_10 || 0)*100).toFixed(2)}%
              </Typography>
              <Typography sx={{ color:'#e0e0e0', fontSize:'0.9rem', mx:.3 }}>·</Typography>
              {/* Breadth */}
              <Typography sx={{ fontSize:'0.75rem', color:'#64748b', fontWeight:500 }}>
                                {t('trendPredictions.regime.breadth')} {((regime.b_adv_5d || 0)*100).toFixed(0)}%
              </Typography>
              <Typography sx={{ color:'#e0e0e0', fontSize:'0.9rem', mx:.3 }}>·</Typography>
              {/* ML status dot */}
              <Box sx={{ display:'flex', alignItems:'center', gap:.4 }}>
                <Box sx={{ width:6, height:6, borderRadius:'50%',
                  bgcolor: regime.market_ok ? '#4caf50' : '#bdbdbd',
                  boxShadow: regime.market_ok ? '0 0 4px rgba(76,175,80,.5)' : 'none',
                }} />
                <Typography sx={{ fontSize:'0.72rem', fontWeight:600,
                  color: regime.market_ok ? '#2e7d32' : '#999' }}>
                  {regime.market_ok ? t('trendPredictions.regime.active') : t('trendPredictions.regime.paused')}
                </Typography>
              </Box>
            </>
          )}
          {/* Calculate button */}
          <Button
            variant="contained"
            startIcon={<CalculateIcon />}
            onClick={handleRecalculate}
            disabled={calculating || refreshing}
            sx={{ ml:1, textTransform:'none', borderRadius:'4px', height:36, fontSize:13, fontWeight:600,
              bgcolor:'#1565c0', '&:hover':{ bgcolor:'#0d47a1' } }}
          >
            {calculating ? t('trendPredictions.recalculate.running') : t('trendPredictions.recalculate.button')}
          </Button>
          {/* Refresh button */}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchData(true)}
            disabled={refreshing || calculating}
            sx={{ textTransform:'none', borderRadius:'4px', height:36, fontSize:13, fontWeight:600 }}
          >
            {t('trendPredictions.regime.refresh')}
          </Button>
        </Box>
      </Box>

      {/* ═══════════════ CALCULATING BANNER ═══════════════ */}
      {calculating && (
        <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb:2, borderRadius:2 }}>
          {t('trendPredictions.recalculate.banner')}
        </Alert>
      )}

      {/* ═══════════════ STALE DATA WARNING ═══════════════ */}
      {(() => {
        if (!predDate || predDate === '—') return null;
        const daysDiff = Math.floor((new Date() - new Date(predDate)) / 86400000);
        if (daysDiff <= 3) return null;
        return (
          <Alert severity="warning" sx={{ mb:2, borderRadius:2 }}>
            {t('trendPredictions.staleDataWarning', { days: daysDiff, date: fmtDate(predDate) })}
          </Alert>
        );
      })()}

      {error && <Alert severity="error" sx={{ mb:2, borderRadius:2 }}>{error}</Alert>}

      {/* ═══════════════ REGIME GUIDANCE ═══════════════ */}
      {regime && (() => {
        const isBear = regime.regime === 'BEAR' || !regime.market_ok;
        const isBull = regime.regime === 'BULL' && regime.market_ok;
        const strongPct = predictions.length > 0 ? (counts.STRONG_CONTINUE / predictions.length * 100) : 0;
        if (isBear) return (
          <Alert severity="warning" variant="outlined"
            icon={<ShieldIcon sx={{ fontSize:20 }} />}
            sx={{ mb:2, borderRadius:2, '& .MuiAlert-message':{ width:'100%' } }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <Box>
                <Typography sx={{ fontWeight:700, fontSize:'0.82rem', color:'#e65100' }}>
                  {t('trendPredictions.guidance.bearTitle')}
                </Typography>
                <Typography sx={{ fontSize:'0.75rem', color:'#8d6e63' }}>
                  {!regime.market_ok ? t('trendPredictions.guidance.bearPaused') : t('trendPredictions.guidance.bearDown')}
                  {' '}{t('trendPredictions.guidance.bearAdvice')}
                  {regime.m_ret_10 < 0 ? t('trendPredictions.guidance.bearVniDrop', { pct: (Math.abs(regime.m_ret_10||0)*100).toFixed(1) }) : ''}
                </Typography>
              </Box>
              <WarningIcon sx={{ color:'#ef6c00', fontSize:28, opacity:.6 }} />
            </Box>
          </Alert>
        );
        if (isBull && strongPct >= 25) return (
          <Alert severity="success" variant="outlined"
            icon={<LightbulbIcon sx={{ fontSize:20 }} />}
            sx={{ mb:2, borderRadius:2, '& .MuiAlert-message':{ width:'100%' } }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <Box>
                <Typography sx={{ fontWeight:700, fontSize:'0.82rem', color:'#2e7d32' }}>
                  {t('trendPredictions.guidance.bullTitle', { pct: strongPct.toFixed(0) })}
                </Typography>
                <Typography sx={{ fontSize:'0.75rem', color:'#558b2f' }}>
                  {t('trendPredictions.guidance.bullAdvice', { breadth: ((regime.b_adv_5d||0)*100).toFixed(0) })}
                </Typography>
              </Box>
              <TrendingUpIcon sx={{ color:'#43a047', fontSize:28, opacity:.5 }} />
            </Box>
          </Alert>
        );
        /* NEUTRAL regime guidance */
        if (regime.regime === 'NEUTRAL') return (
          <Alert severity="info" variant="outlined"
            icon={<BalanceIcon sx={{ fontSize:20 }} />}
            sx={{ mb:2, borderRadius:2, '& .MuiAlert-message':{ width:'100%' } }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <Box>
                <Typography sx={{ fontWeight:700, fontSize:'0.82rem', color:'#1565c0' }}>
                  {t('trendPredictions.guidance.neutralTitle')}
                </Typography>
                <Typography sx={{ fontSize:'0.75rem', color:'#546e7a' }}>
                  {t('trendPredictions.guidance.neutralAdvice')}
                </Typography>
              </Box>
              <BalanceIcon sx={{ color:'#1565c0', fontSize:28, opacity:.4 }} />
            </Box>
          </Alert>
        );
        return null;
      })()}

      {/* ═══════════════ METRICS ROW ═══════════════ */}
      <Box sx={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:1.5, mb:2 }}>
        {[
          { key:null,               labelKey:'total',     c:'#1565c0', dot:null },
          { key:'STRONG_CONTINUE',  labelKey:'strong',    c:'#2e7d32', dot:'#4caf50' },
          { key:'CONTINUE',         labelKey:'continue',  c:'#e65100', dot:'#ff9800' },
          { key:'WEAKENING',        labelKey:'weakening', c:'#d84315', dot:'#ff5722' },
          { key:'REVERSAL',         labelKey:'reversal',  c:'#616161', dot:'#9e9e9e' },
        ].map(card => {
          const n = card.key ? (counts[card.key] || 0) : predictions.length;
          const pct = card.key && predictions.length > 0 ? (n / predictions.length * 100) : null;
          const isActive = card.key && vFilter === card.key;
          const accV = card.key ? accuracy?.by_verdict?.[card.key] : null;
          return (
            <Paper key={card.key||'total'} elevation={isActive ? 2 : 0}
              onClick={() => { if (card.key) { setVFilter(isActive ? 'ALL' : card.key); setPage(0); } }}
              sx={{
                borderRadius:2, overflow:'hidden', cursor: card.key ? 'pointer' : 'default',
                border: isActive ? `1.5px solid ${rgba(card.c,.5)}` : `1px solid ${rgba(card.c,.08)}`,
                bgcolor: isActive ? rgba(card.c,.04) : '#fff',
                transition:'all .2s cubic-bezier(.4,0,.2,1)',
                '&:hover': card.key ? { borderColor: rgba(card.c,.3), bgcolor: rgba(card.c,.02),
                  transform:'translateY(-1px)', boxShadow:`0 4px 16px ${rgba(card.c,.08)}` } : {},
              }}>
              {/* Top accent line */}
              <Box sx={{ height:2.5, background:`linear-gradient(90deg, ${card.c}, ${rgba(card.c,.3)})` }} />
              <Box sx={{ px:1.3, pt:.8, pb:1 }}>
                {/* Label row */}
                <Box sx={{ display:'flex', alignItems:'center', gap:.4, mb:.4 }}>
                  {card.dot && <Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor: card.dot }} />}
                  <Typography sx={{ fontSize:'0.58rem', fontWeight:600, color:'#aaa', textTransform:'uppercase', letterSpacing:.6 }}>
                    {t(`trendPredictions.cards.${card.labelKey}`)}
                  </Typography>
                </Box>
                {/* Number + pct */}
                <Box sx={{ display:'flex', alignItems:'baseline', gap:.4 }}>
                  <Typography sx={{ fontSize:'1.7rem', fontWeight:800, color: card.c, lineHeight:1, fontFeatureSettings:'"tnum"' }}>
                    {n}
                  </Typography>
                  {pct != null && (
                    <Typography sx={{ fontSize:'0.65rem', fontWeight:600, color: rgba(card.c,.45) }}>
                      {pct.toFixed(0)}%
                    </Typography>
                  )}
                </Box>
                {/* Bottom context line */}
                {!card.key ? (
                  <Typography sx={{ fontSize:'0.6rem', color:'#999', mt:.4, lineHeight:1.3 }}>
                    {t('trendPredictions.cards.avgScore')} <b style={{ color:'#1565c0' }}>{distribution?.avg_score?.toFixed(1)||'—'}</b>
                    {' · '}{t('trendPredictions.cards.median')} <b style={{ color:'#1565c0' }}>{distribution?.median_score?.toFixed(0)||'—'}</b>
                  </Typography>
                ) : accV?.avg_return_10d != null ? (
                  <Box sx={{ display:'flex', alignItems:'center', gap:.4, mt:.4 }}>
                    <Typography sx={{ fontSize:'0.6rem', fontWeight:700,
                      color: accV.avg_return_10d >= 0 ? '#2e7d32' : '#c62828' }}>
                      {accV.avg_return_10d >= 0 ? '+' : ''}{accV.avg_return_10d}%
                    </Typography>
                    <Typography sx={{ fontSize:'0.55rem', color:'#bbb' }}>{t('trendPredictions.cards.perTenDays')}</Typography>
                    {accV.risk_reward != null && (
                      <Typography sx={{ fontSize:'0.55rem', fontWeight:600, color:'#999', ml:.2 }}>
                        R/R {accV.risk_reward}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize:'0.58rem', color:'#ccc', mt:.4 }}>—</Typography>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* ═══════════════ DISTRIBUTION + PROPORTION ═══════════════ */}
      <Paper elevation={0} sx={{ p:1.5, mb:2, border:'1px solid #e8e8e8', borderRadius:2 }}>
        {/* Header row */}
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:.8 }}>
          <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:'#555' }}>
            {t('trendPredictions.distribution.title')}
          </Typography>
          <Typography sx={{ fontSize:'0.68rem', color:'#999' }}>
            {t('trendPredictions.distribution.median', { val: distribution?.median_score?.toFixed(0)||'—' })} · {distribution?.avg_weighted_p ? t('trendPredictions.distribution.avgP', { val: (distribution.avg_weighted_p*100).toFixed(1)+'%' }) : '—'}
          </Typography>
        </Box>

        {/* Combined chart: histogram with zone backgrounds */}
        <Box sx={{ position:'relative' }}>
          {/* Zone background bands */}
          <Box sx={{ position:'absolute', top:0, left:0, right:0, bottom:0, display:'flex', borderRadius:1, overflow:'hidden', zIndex:0 }}>
            {[
              { w: '40%', c:'rgba(158,158,158,.04)' },
              { w: '15%', c:'rgba(255,87,34,.04)' },
              { w: '20%', c:'rgba(255,152,0,.04)' },
              { w: '25%', c:'rgba(76,175,80,.04)' },
            ].map((z,i) => <Box key={i} sx={{ width:z.w, bgcolor:z.c }} />)}
          </Box>
          <Box sx={{ position:'relative', zIndex:1 }}>
            <DistributionChart histogram={distribution?.score_histogram} total={predictions.length} />
          </Box>
        </Box>

        {/* Score scale labels */}
        <Box sx={{ display:'flex', justifyContent:'space-between', mt:.3, mb:1 }}>
          {['0','20','40','55','75','100'].map(l => (
            <Typography key={l} sx={{ fontSize:'0.58rem', color:'#ccc', fontWeight:500 }}>{l}</Typography>
          ))}
        </Box>

        {/* Proportion bar (integrated, no separate legend needed) */}
        <ProportionBar counts={counts} total={predictions.length} activeFilter={vFilter} onFilter={(v) => { setVFilter(v); setPage(0); }} />
      </Paper>

      {/* ═══════════════ TOP PICKS ═══════════════ */}
      {(() => {
        const topPicks = predictions.filter(p => p.verdict === 'STRONG_CONTINUE' && p.market_ok).slice(0, 5);
        if (!topPicks.length) return null;
        return (
          <Paper elevation={0} sx={{ p:1.5, mb:2, border:'1px solid #e8e8e8', borderRadius:2,
            background:'linear-gradient(135deg, rgba(46,125,50,.02) 0%, #fff 100%)' }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:.8, mb:1 }}>
              <TrophyIcon sx={{ fontSize:16, color:'#f9a825' }} />
              <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'#555' }}>{t('trendPredictions.topPicks.title')}</Typography>
            </Box>
            <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap' }}>
              {topPicks.map((p, i) => (
                <Box key={p.symbol}
                  onClick={() => navigate(`/stocks/${p.symbol}`)}
                  sx={{
                    display:'flex', alignItems:'center', gap:1, px:1.5, py:.8,
                    borderRadius:2, cursor:'pointer', transition:'all .15s',
                    border:'1px solid', borderColor: rgba('#2e7d32', .12),
                    bgcolor: rgba('#2e7d32', .02),
                    '&:hover':{ bgcolor: rgba('#2e7d32', .06), borderColor: rgba('#2e7d32', .3), transform:'translateY(-1px)',
                      boxShadow:`0 3px 12px ${rgba('#2e7d32', .1)}` },
                  }}>
                  <Typography sx={{ fontSize:'0.68rem', fontWeight:800, color:'#f9a825', lineHeight:1 }}>#{i+1}</Typography>
                  <Box>
                    <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:'#1a1a1a', lineHeight:1.2 }}>{p.symbol}</Typography>
                    <Typography sx={{ fontSize:'0.62rem', color:'#888', lineHeight:1.1 }}>
                      {t('trendPredictions.topPicks.detail', { score: p.composite_score.toFixed(0), pct: (p.weighted_p*100).toFixed(0) })}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        );
      })()}

      {/* ═══════════════ FILTER BAR ═══════════════ */}
      <Box sx={{ display:'flex', gap:1.5, mb:1.5, alignItems:'center', flexWrap:'wrap' }}>
        <TextField size="small" placeholder={t('trendPredictions.filter.searchPlaceholder')}
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize:18, color:'#aaa' }} /></InputAdornment> }}
          sx={{ width:160, '& .MuiOutlinedInput-root':{ borderRadius:2, height:34, fontSize:'0.82rem' } }}
        />
        {/* quick filter chips */}
        {[
          { key:'ALL', label: t('trendPredictions.filter.all', { count: predictions.length }), c:'#555', dot:null },
          { key:'STRONG_CONTINUE', label: t('trendPredictions.filter.strong', { count: counts.STRONG_CONTINUE }), c:'#2e7d32', dot:'#4caf50' },
          { key:'CONTINUE',        label: t('trendPredictions.filter.continue', { count: counts.CONTINUE }),    c:'#e65100', dot:'#ff9800' },
          { key:'WEAKENING',       label: t('trendPredictions.filter.weakening', { count: counts.WEAKENING }),    c:'#d84315', dot:'#ff5722' },
          { key:'REVERSAL',        label: t('trendPredictions.filter.reversal', { count: counts.REVERSAL }),   c:'#616161', dot:'#9e9e9e' },
        ].map(f => (
          <Chip key={f.key} size="small"
            icon={f.dot ? <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor: f.dot, ml:'8px!important' }} /> : undefined}
            label={f.label}
            onClick={() => { setVFilter(f.key); setPage(0); }}
            sx={{
              height:28, fontSize:'0.75rem', fontWeight: vFilter === f.key ? 700 : 500, cursor:'pointer',
              bgcolor: vFilter === f.key ? rgba(f.c, .12) : 'transparent',
              border: `1px solid ${vFilter === f.key ? rgba(f.c, .4) : '#e0e0e0'}`,
              color: vFilter === f.key ? f.c : '#888',
              '&:hover':{ bgcolor: rgba(f.c, .06) },
            }} />
        ))}
        <Box sx={{ flex:1 }} />
        <Typography sx={{ fontSize:'0.75rem', color:'#aaa' }}>{t('trendPredictions.filter.results', { count: filtered.length })}</Typography>
      </Box>

      {/* ═══════════════ DATA TABLE ═══════════════ */}
      <Paper elevation={0} sx={{ border:'1px solid #e8e8e8', borderRadius:2, overflow:'hidden' }}>
        <TableContainer sx={{ overflow:'visible' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  { id:'rank',            label: t('trendPredictions.columns.rank'),       w:'4%',  tip:null },
                  { id:'symbol',          label: t('trendPredictions.columns.symbol'),     w:'10%', tip:null },
                  { id:'composite_score', label: t('trendPredictions.columns.score'),      w:'16%', tip: t('trendPredictions.columnTips.score') },
                  { id:'score_delta',     label: t('trendPredictions.columns.delta'),      w:'7%',  tip: t('trendPredictions.columnTips.delta') },
                  { id:'weighted_p',      label: t('trendPredictions.columns.pCont'),      w:'10%', tip: t('trendPredictions.columnTips.pCont') },
                  { id:'verdict',         label: t('trendPredictions.columns.signal'),     w:'12%', tip: t('trendPredictions.columnTips.signal') },
                  { id:'expected',        label: t('trendPredictions.columns.expected10d'),w:'15%', tip: t('trendPredictions.columnTips.expected10d') },
                ].map(col => (
                  <TableCell key={col.id} sx={{
                    fontWeight:700, fontSize:'0.7rem', bgcolor:'#fafafa', width: col.w,
                    borderBottom:'2px solid #eee', textTransform:'uppercase', letterSpacing:.5, color:'#888', py:.8,
                  }}>
                    <TableSortLabel active={orderBy===col.id} direction={orderBy===col.id?orderDir:'asc'}
                      onClick={() => handleSort(col.id)}>
                      {col.tip ? (
                        <Tooltip title={col.tip} arrow placement="top">
                          <span style={{ borderBottom:'1px dashed #ccc', cursor:'help' }}>{col.label}</span>
                        </Tooltip>
                      ) : col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.map((row, idx) => {
                const cfg = V[row.verdict] || V.REVERSAL;
                const top = row.rank <= 3;
                const even = idx % 2 === 0;
                return (
                  <TableRow key={row.symbol} hover
                    onClick={() => navigate(`/stocks/${row.symbol}`)}
                    sx={{
                      cursor:'pointer', transition:'all .12s',
                      bgcolor: top ? rgba('#2e7d32',.03) : even ? 'rgba(0,0,0,.008)' : 'transparent',
                      '&:hover':{ bgcolor: rgba('#1565c0',.05), '& td:first-of-type':{ borderLeft:'2px solid #1565c0' } },
                      '& td':{ borderColor:'#f5f5f5', py:.7 },
                    }}>
                    <TableCell>
                      {row.rank <= 3 ? (
                        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'center',
                          width:22, height:22, borderRadius:'50%',
                          background: row.rank === 1 ? 'linear-gradient(135deg,#ffd54f,#f9a825)'
                            : row.rank === 2 ? 'linear-gradient(135deg,#e0e0e0,#9e9e9e)'
                            : 'linear-gradient(135deg,#ffcc80,#ef6c00)',
                          boxShadow: `0 1px 4px ${rgba(row.rank === 1 ? '#f9a825' : row.rank === 2 ? '#9e9e9e' : '#ef6c00', .3)}`,
                        }}>
                          <Typography sx={{ fontWeight:800, color:'#fff', fontSize:'0.65rem', textShadow:'0 1px 2px rgba(0,0,0,.2)' }}>
                            {row.rank}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ fontWeight:500, color:'#999', fontSize:'0.8rem' }}>
                          {row.rank}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display:'flex', alignItems:'center', gap:.6 }}>
                        <Typography sx={{ fontWeight:700, color:'#1a1a1a', fontSize: top ? '0.88rem' : '0.82rem' }}>
                          {row.symbol}
                        </Typography>
                        <IconButton size="small"
                          onClick={e => { e.stopPropagation(); setHistSym(row.symbol); }}
                          sx={{ opacity:0, transition:'opacity .15s', p:.3,
                            'tr:hover &':{ opacity:.5 }, '&:hover':{ opacity:'1!important' } }}>
                          <TimelineIcon sx={{ fontSize:14 }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell><ScoreBar score={row.composite_score} wide={top} /></TableCell>
                    <TableCell>
                      {row.score_delta != null && row.score_delta !== 0 ? (
                        <Typography sx={{ fontWeight:700, fontSize:'0.78rem',
                          color: row.score_delta > 0 ? '#2e7d32' : '#c62828' }}>
                          {row.score_delta > 0 ? '+' : ''}{row.score_delta.toFixed(1)}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize:'0.75rem', color:'#ddd' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight:600, color: cfg.c, fontSize:'0.8rem' }}>
                        {(row.weighted_p*100).toFixed(1)}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={t(`trendPredictions.verdict.${V[row.verdict] ? row.verdict : 'REVERSAL'}`)} size="small"
                        sx={{ bgcolor: cfg.bg, color: cfg.c, fontWeight:600, fontSize:'0.68rem', height:22,
                          border:`1px solid ${rgba(cfg.c, .15)}` }} />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const er = row.expected_return;
                        if (!er) return <Typography sx={{ fontSize:'0.72rem', color:'#ccc' }}>—</Typography>;
                        const { avg_ret: avg, p25, p75, med_ret, max_gain, max_dd, risk_reward: rr, p_up: up, n } = er;
                        const pUp = up || 0;
                        const isPos = (avg || 0) >= 0;
                        return (
                          <Tooltip arrow placement="left" title={
                            <Box sx={{ fontSize:'0.72rem', lineHeight:1.7, minWidth:180 }}>
                              <Box sx={{ fontWeight:700, mb:.3 }}>{t('trendPredictions.expected.title', { score: row.composite_score.toFixed(0) })}</Box>
                              <Box>{t('trendPredictions.expected.avgReturn')} <b style={{ color: isPos ? '#4caf50' : '#ef5350' }}>{isPos ? '+' : ''}{avg?.toFixed(2)}%</b></Box>
                              <Box>{t('trendPredictions.expected.median')} <b>{(med_ret||0) >= 0 ? '+' : ''}{med_ret?.toFixed(2)}%</b></Box>
                              <Box>{t('trendPredictions.expected.range50')} <b>{p25?.toFixed(1)}%</b> {t('trendPredictions.expected.to')} <b>{p75 >= 0 ? '+' : ''}{p75?.toFixed(1)}%</b></Box>
                              <Box>{t('trendPredictions.expected.pUp')} <b>{pUp.toFixed(1)}%</b></Box>
                              <Box sx={{ mt:.3, pt:.3, borderTop:'1px solid rgba(255,255,255,.15)' }}>
                                {t('trendPredictions.expected.maxGain')} <span style={{color:'#81c784'}}>+{max_gain?.toFixed(1)}%</span><br/>
                                {t('trendPredictions.expected.maxDD')} <span style={{color:'#ef9a9a'}}>{max_dd?.toFixed(1)}%</span><br/>
                                {t('trendPredictions.expected.riskReward')} <b>{rr?.toFixed(2)}</b> · {t('trendPredictions.expected.samples', { n })}
                              </Box>
                            </Box>
                          }>
                            <Box sx={{ cursor:'help' }}>
                              {/* Main return number */}
                              <Box sx={{ display:'flex', alignItems:'baseline', gap:.5 }}>
                                <Typography sx={{ fontSize:'0.95rem', fontWeight:800, lineHeight:1.2,
                                  color: isPos ? '#2e7d32' : '#c62828' }}>
                                  {isPos ? '+' : ''}{avg?.toFixed(1)}%
                                </Typography>
                                {rr != null && (
                                  <Typography sx={{ fontSize:'0.58rem', fontWeight:700, color: rr >= 1 ? '#1565c0' : '#999',
                                    bgcolor: rr >= 1 ? 'rgba(21,101,192,.06)' : 'rgba(0,0,0,.03)',
                                    px:.4, borderRadius:.5, lineHeight:'14px' }}>
                                    {rr?.toFixed(1)}x
                                  </Typography>
                                )}
                              </Box>
                              {/* P(up) mini gauge */}
                              <Box sx={{ display:'flex', alignItems:'center', gap:.5, mt:.3 }}>
                                <Box sx={{ flex:1, height:4, borderRadius:2, bgcolor:'#f0f0f0', overflow:'hidden', maxWidth:60 }}>
                                  <Box sx={{ width:`${pUp}%`, height:'100%', borderRadius:2,
                                    background: pUp >= 55 ? 'linear-gradient(90deg, #66bb6a, #43a047)'
                                      : pUp >= 45 ? 'linear-gradient(90deg, #ffa726, #fb8c00)'
                                      : 'linear-gradient(90deg, #ef5350, #e53935)'
                                  }} />
                                </Box>
                                <Typography sx={{ fontSize:'0.6rem', fontWeight:600,
                                  color: pUp >= 55 ? '#2e7d32' : pUp >= 45 ? '#e65100' : '#c62828' }}>
                                  ↑{pUp.toFixed(0)}%
                                </Typography>
                              </Box>
                            </Box>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                  </TableRow>
                );
              })}
              {pageData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py:5 }}>
                    <Typography color="textSecondary" sx={{ fontSize:'0.85rem' }}>
                      {search ? t('trendPredictions.empty.notFound', { search }) : vFilter !== 'ALL' ? t('trendPredictions.empty.noVerdict', { verdict: t(`trendPredictions.verdict.${vFilter}`) }) : t('trendPredictions.empty.noData')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(_,p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRPP(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10,25,50,100]}
          labelRowsPerPage={t('trendPredictions.pagination.rowsPerPage')} labelDisplayedRows={({from,to,count}) => t('trendPredictions.pagination.displayedRows', { from, to, count })}
          sx={{ borderTop:'1px solid #f0f0f0', '& .MuiTablePagination-toolbar':{ minHeight:40 } }}
        />
      </Paper>

      {/* ═══════════════ ACCURACY TRACKING PANEL ═══════════════ */}
      <Paper elevation={0} sx={{ border:'1px solid #e8e8e8', borderRadius:2, overflow:'hidden', mt:2 }}>
        <Box onClick={() => setShowAccuracy(!showAccuracy)}
          sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', px:2, py:1.2, cursor:'pointer',
            '&:hover':{ bgcolor:'rgba(0,0,0,.015)' }, transition:'background .15s' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <AssessmentIcon sx={{ fontSize:18, color:'#667eea' }} />
            <Typography sx={{ fontSize:'0.85rem', fontWeight:700, color:'#1e293b' }}>{t('trendPredictions.accuracy.title')}</Typography>
            {accuracy?.overall?.accuracy_10d != null && (
              <Box sx={{ display:'flex', gap:.5, alignItems:'center' }}>
                <Chip size="small" label={t('trendPredictions.accuracy.correctChip', { pct: accuracy.overall.accuracy_10d })}
                  sx={{ height:20, fontSize:'0.65rem', fontWeight:600,
                    bgcolor: accuracy.overall.accuracy_10d >= 50 ? '#e8f5e9' : '#fff3e0',
                    color: accuracy.overall.accuracy_10d >= 50 ? '#2e7d32' : '#e65100' }} />
                {accuracy.overall.risk_reward != null && (
                  <Chip size="small" label={t('trendPredictions.accuracy.rrChip', { val: accuracy.overall.risk_reward })}
                    sx={{ height:20, fontSize:'0.65rem', fontWeight:600,
                      bgcolor: accuracy.overall.risk_reward >= 1 ? '#e3f2fd' : '#fce4ec',
                      color: accuracy.overall.risk_reward >= 1 ? '#1565c0' : '#c62828' }} />
                )}
                {accuracy.overall.strong_avg_return_10d != null && (
                  <Chip size="small" label={t('trendPredictions.accuracy.strongChip', { val: `${accuracy.overall.strong_avg_return_10d >= 0 ? '+' : ''}${accuracy.overall.strong_avg_return_10d}` })}
                    sx={{ height:20, fontSize:'0.65rem', fontWeight:600,
                      bgcolor: accuracy.overall.strong_avg_return_10d >= 0 ? '#e8f5e9' : '#fce4ec',
                      color: accuracy.overall.strong_avg_return_10d >= 0 ? '#2e7d32' : '#c62828' }} />
                )}
              </Box>
            )}
          </Box>
          {showAccuracy ? <CollapseIcon sx={{ fontSize:20, color:'#999' }} /> : <ExpandIcon sx={{ fontSize:20, color:'#999' }} />}
        </Box>

        {showAccuracy && accError && (
          <Box sx={{ px:2, pb:2, borderTop:'1px solid #f0f0f0' }}>
            <Alert severity="warning" sx={{ mt:1, borderRadius:2, fontSize:'0.78rem' }}>
              {t('trendPredictions.accuracy.errorMsg')}
            </Alert>
          </Box>
        )}

        {showAccuracy && accuracy && (
          <Box sx={{ px:2, pb:2, borderTop:'1px solid #f0f0f0' }}>
            {/* Date range info */}
            <Typography sx={{ fontSize:'0.68rem', color:'#999', mt:1, mb:1.5 }}>
              {t('trendPredictions.accuracy.evalCount', { count: accuracy.overall.total_evaluated?.toLocaleString() })}
              {accuracy.date_range?.from ? t('trendPredictions.accuracy.dateRange', { from: fmtDate(accuracy.date_range.from), to: fmtDate(accuracy.date_range.to) }) : ''}
              {accuracy.date_range?.n_dates ? t('trendPredictions.accuracy.sessions', { n: accuracy.date_range.n_dates }) : ''}
            </Typography>

            {/* Overall metrics dashboard */}
            <Box sx={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:1.5, mb:2 }}>
              {[
                { label: t('trendPredictions.accuracy.metrics.correct10d'), val: accuracy.overall.accuracy_10d, c:'#7b1fa2', suffix:'%',
                  gauge: accuracy.overall.accuracy_10d, maxGauge: 100,
                  tip: t('trendPredictions.accuracy.metricTips.correct10d') },
                { label: t('trendPredictions.accuracy.metrics.strongReturn'),  val: accuracy.overall.strong_avg_return_10d, c:'#2e7d32', suffix:'%',
                  sub: accuracy.overall.strong_avg_return_10d != null ? t('trendPredictions.accuracy.approxYearly', { pct: (accuracy.overall.strong_avg_return_10d * 26).toFixed(0) }) : null,
                  tip: t('trendPredictions.accuracy.metricTips.strongReturn') },
                { label: t('trendPredictions.accuracy.metrics.riskReward'), val: accuracy.overall.risk_reward, c:'#1565c0', suffix:'x',
                  gauge: accuracy.overall.risk_reward ? Math.min(accuracy.overall.risk_reward / 2 * 100, 100) : 0, maxGauge: 100,
                  tip: t('trendPredictions.accuracy.metricTips.riskReward') },
                { label: t('trendPredictions.accuracy.metrics.maxDD'),  val: accuracy.overall.avg_max_drawdown, c:'#c62828', suffix:'%',
                  tip: t('trendPredictions.accuracy.metricTips.maxDD') },
                { label: t('trendPredictions.accuracy.metrics.maxGain'), val: accuracy.overall.avg_max_gain, c:'#2e7d32', suffix:'%',
                  tip: t('trendPredictions.accuracy.metricTips.maxGain') },
              ].map((m, i) => (
                <Tooltip key={i} title={m.tip} arrow>
                  <Box sx={{ px:1.5, py:1.2, borderRadius:2,
                    border:`1px solid ${rgba(m.c, .1)}`, bgcolor: rgba(m.c, .015),
                    transition:'all .15s', '&:hover':{ bgcolor: rgba(m.c, .04), borderColor: rgba(m.c, .25) } }}>
                    <Typography sx={{ fontSize:'0.58rem', fontWeight:600, color:'#999', textTransform:'uppercase', letterSpacing:.5, mb:.2 }}>
                      {m.label}
                    </Typography>
                    <Box sx={{ display:'flex', alignItems:'baseline', gap:.5 }}>
                      <Typography sx={{ fontSize:'1.3rem', fontWeight:800, color: m.c, lineHeight:1.2, fontFeatureSettings:'"tnum"' }}>
                        {m.val != null ? `${m.val}${m.suffix}` : '—'}
                      </Typography>
                      {m.sub && (
                        <Typography sx={{ fontSize:'0.58rem', color:'#888', fontWeight:600 }}>
                          {m.sub}
                        </Typography>
                      )}
                    </Box>
                    {m.gauge != null && (
                      <Box sx={{ mt:.5, height:3, borderRadius:2, bgcolor:'rgba(0,0,0,.04)', overflow:'hidden' }}>
                        <Box sx={{ width:`${m.gauge}%`, height:'100%', borderRadius:2,
                          background:`linear-gradient(90deg, ${rgba(m.c,.3)}, ${m.c})` }} />
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              ))}
            </Box>

            {/* Per-verdict accuracy table */}
            <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:'#555', mb:.8 }}>{t('trendPredictions.accuracy.bySignalTitle')}</Typography>
            <Box sx={{ overflowX:'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['signal','count','avgScore','correct5d','correct10d','return5d','return10d','return20d','rr','avgDD','avgGain'].map(h => (
                      <TableCell key={h} sx={{ fontWeight:700, fontSize:'0.62rem', color:'#999', py:.5,
                        textTransform:'uppercase', letterSpacing:.3, whiteSpace:'nowrap' }}>{t(`trendPredictions.accuracy.signalHeaders.${h}`)}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {['STRONG_CONTINUE','CONTINUE','WEAKENING','REVERSAL'].map(v => {
                    const d = accuracy.by_verdict?.[v];
                    if (!d) return null;
                    const vCfg = V[v] || { c:'#666', bg:'#f5f5f5' };
                    return (
                      <TableRow key={v} sx={{ '&:last-child td':{ border:0 } }}>
                        <TableCell sx={{ py:.5 }}>
                          <Box sx={{ display:'flex', alignItems:'center', gap:.5 }}>
                            <Box sx={{ width:6, height:6, borderRadius:'50%', bgcolor: vCfg.dot || vCfg.c }} />
                            <Typography sx={{ fontSize:'0.72rem', fontWeight:600, color: vCfg.c }}>{t(`trendPredictions.verdict.${v}`)}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize:'0.75rem', py:.5, fontWeight:600 }}>{d.count}</TableCell>
                        <TableCell sx={{ fontSize:'0.75rem', py:.5 }}>{d.avg_score}</TableCell>
                        <TableCell sx={{ py:.5 }}>
                          {d.accuracy_5d != null ? (
                            <Box sx={{ display:'flex', alignItems:'center', gap:.5 }}>
                              <Box sx={{ width:28, height:4, borderRadius:2, bgcolor:'#eee', overflow:'hidden' }}>
                                <Box sx={{ width:`${d.accuracy_5d}%`, height:'100%', borderRadius:2,
                                  bgcolor: d.accuracy_5d >= 50 ? '#4caf50' : '#ef5350' }} />
                              </Box>
                              <Typography sx={{ fontSize:'0.72rem', fontWeight:700,
                                color: d.accuracy_5d >= 50 ? '#2e7d32' : '#c62828' }}>{d.accuracy_5d}%</Typography>
                            </Box>
                          ) : '—'}
                        </TableCell>
                        <TableCell sx={{ py:.5 }}>
                          {d.accuracy_10d != null ? (
                            <Box sx={{ display:'flex', alignItems:'center', gap:.5 }}>
                              <Box sx={{ width:28, height:4, borderRadius:2, bgcolor:'#eee', overflow:'hidden' }}>
                                <Box sx={{ width:`${d.accuracy_10d}%`, height:'100%', borderRadius:2,
                                  bgcolor: d.accuracy_10d >= 50 ? '#4caf50' : '#ef5350' }} />
                              </Box>
                              <Typography sx={{ fontSize:'0.72rem', fontWeight:700,
                                color: d.accuracy_10d >= 50 ? '#2e7d32' : '#c62828' }}>{d.accuracy_10d}%</Typography>
                            </Box>
                          ) : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize:'0.75rem', py:.5,
                          color: d.avg_return_5d >= 0 ? '#2e7d32' : '#c62828' }}>
                          {d.avg_return_5d != null ? `${d.avg_return_5d >= 0 ? '+' : ''}${d.avg_return_5d}%` : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize:'0.75rem', py:.5,
                          color: d.avg_return_10d >= 0 ? '#2e7d32' : '#c62828' }}>
                          {d.avg_return_10d != null ? `${d.avg_return_10d >= 0 ? '+' : ''}${d.avg_return_10d}%` : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize:'0.75rem', py:.5,
                          color: d.avg_return_20d >= 0 ? '#2e7d32' : '#c62828' }}>
                          {d.avg_return_20d != null ? `${d.avg_return_20d >= 0 ? '+' : ''}${d.avg_return_20d}%` : '—'}
                        </TableCell>
                        <TableCell sx={{ py:.5 }}>
                          {d.risk_reward != null ? (
                            <Chip size="small" label={`${d.risk_reward}x`}
                              sx={{ height:18, fontSize:'0.62rem', fontWeight:700,
                                bgcolor: d.risk_reward >= 1.2 ? '#e8f5e9' : d.risk_reward >= 1 ? '#e3f2fd' : '#ffebee',
                                color: d.risk_reward >= 1.2 ? '#2e7d32' : d.risk_reward >= 1 ? '#1565c0' : '#c62828',
                              }} />
                          ) : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize:'0.75rem', py:.5, color:'#c62828' }}>
                          {d.avg_drawdown != null ? `${d.avg_drawdown}%` : '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize:'0.75rem', py:.5, color:'#2e7d32' }}>
                          {d.avg_gain != null ? `+${d.avg_gain}%` : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>

            {/* Context note */}
            {accuracy.overall.strong_avg_return_10d != null && (
              <Box sx={{ mt:1.5, px:1.5, py:1, borderRadius:1.5, bgcolor:'#f8f9fa', border:'1px solid #eee' }}>
                <Typography sx={{ fontSize:'0.68rem', color:'#666', lineHeight:1.6 }}>
                  <b>{t('trendPredictions.accuracy.contextLabel')}</b>{t('trendPredictions.accuracy.contextNote')}
                  {accuracy.overall.strong_avg_return_10d != null && t('trendPredictions.accuracy.contextStrong', { val: accuracy.overall.strong_avg_return_10d })}
                  {accuracy.overall.strong_avg_return_10d != null && t('trendPredictions.accuracy.contextYearly', { pct: (accuracy.overall.strong_avg_return_10d * 26).toFixed(0) })}
                  {' '}{t('trendPredictions.accuracy.contextRR')}
                </Typography>
              </Box>
            )}

            {/* Daily accuracy chart — normalized around 50% baseline */}
            {dailyAcc.length > 0 && (
              <Box sx={{ mt:2 }}>
                <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:'#555', mb:.8 }}>{t('trendPredictions.accuracy.dailyChartTitle')}</Typography>
                <Box sx={{ position:'relative', height:80 }}>
                  {/* 50% baseline — thin dashed */}
                  <Box sx={{ position:'absolute', top:'50%', left:0, right:0, borderTop:'1px dashed rgba(0,0,0,.12)', zIndex:1 }} />
                  <Box sx={{ position:'absolute', top:'50%', right:4, transform:'translateY(-50%)', zIndex:2 }}>
                    <Typography sx={{ fontSize:'0.5rem', color:'#bbb' }}>50%</Typography>
                  </Box>
                  <Box sx={{ display:'flex', alignItems:'flex-end', gap:'2px', height:'100%', position:'relative', zIndex:3 }}>
                    {dailyAcc.map((d, i) => {
                      const val = d.accuracy_10d;
                      if (val == null) return <Box key={i} sx={{ flex:1, height:0 }} />;
                      const c = val >= 55 ? '#43a047' : val >= 48 ? '#ff9800' : '#e53935';
                      // normalize: 20% → 0, 80% → 100%  (50% baseline = 50% height)
                      const norm = Math.max(0, Math.min(100, ((val - 20) / 60) * 100));
                      return (
                        <Tooltip key={i} title={`${fmtDate(d.date)}: ${val.toFixed(1)}% (n=${d.n_stocks})`} arrow>
                          <Box sx={{
                            flex:1, height:`${Math.max(norm, 5)}%`, minHeight:3,
                            bgcolor: c, borderRadius:'2px 2px 0 0',
                            opacity:.85, transition:'all .15s', cursor:'pointer',
                            '&:hover':{ opacity:1, transform:'scaleY(1.08)' },
                          }} />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
                <Box sx={{ display:'flex', justifyContent:'space-between', mt:.3 }}>
                  <Typography sx={{ fontSize:'0.58rem', color:'#ccc' }}>{fmtDate(dailyAcc[0]?.date)}</Typography>
                  <Typography sx={{ fontSize:'0.58rem', color:'#ccc' }}>{fmtDate(dailyAcc[dailyAcc.length-1]?.date)}</Typography>
                </Box>
              </Box>
            )}

            {/* ═══════════ PER-STOCK ACCURACY ═══════════ */}
            {stockAcc.length > 0 && (
              <Box sx={{ mt:2.5 }}>
                <Typography sx={{ fontSize:'0.75rem', fontWeight:700, color:'#555', mb:.8 }}>
                  {t('trendPredictions.accuracy.topStocksTitle', { count: stockAcc.length })}
                </Typography>
                <Box sx={{ overflowX:'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['symbol','appearances','strong','avgScore','correct10d','return10d','avgDD','avgGain'].map(h => (
                          <TableCell key={h} sx={{ fontWeight:700, fontSize:'0.62rem', color:'#999', py:.5,
                            textTransform:'uppercase', letterSpacing:.3, whiteSpace:'nowrap' }}>{t(`trendPredictions.accuracy.stockHeaders.${h}`)}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stockAcc.map((s, i) => (
                        <TableRow key={s.symbol} hover sx={{ cursor:'pointer', '&:last-child td':{ border:0 } }}
                          onClick={() => navigate(`/stocks/${s.symbol}`)}>
                          <TableCell sx={{ py:.5 }}>
                            <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'#1a1a1a' }}>{s.symbol}</Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize:'0.75rem', py:.5 }}>{s.appearances}</TableCell>
                          <TableCell sx={{ fontSize:'0.75rem', py:.5, fontWeight:600, color:'#2e7d32' }}>{s.strong_count}</TableCell>
                          <TableCell sx={{ fontSize:'0.75rem', py:.5 }}>{s.avg_score}</TableCell>
                          <TableCell sx={{ fontSize:'0.75rem', py:.5, fontWeight:700,
                            color: (s.accuracy_10d||0) >= 50 ? '#2e7d32' : '#c62828' }}>
                            {s.accuracy_10d != null ? `${s.accuracy_10d}%` : '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize:'0.75rem', py:.5,
                            color: (s.avg_return_10d||0) >= 0 ? '#2e7d32' : '#c62828' }}>
                            {s.avg_return_10d != null ? `${s.avg_return_10d >= 0 ? '+' : ''}${s.avg_return_10d}%` : '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize:'0.75rem', py:.5, color:'#c62828' }}>
                            {s.avg_drawdown != null ? `${s.avg_drawdown}%` : '—'}
                          </TableCell>
                          <TableCell sx={{ fontSize:'0.75rem', py:.5, color:'#2e7d32' }}>
                            {s.avg_gain != null ? `+${s.avg_gain}%` : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* ═══════════════ RETURN PROFILE PANEL ═══════════════ */}
      <Paper elevation={0} sx={{ border:'1px solid #e8e8e8', borderRadius:2, overflow:'hidden', mt:2 }}>
        <Box onClick={() => setShowReturnProfile(!showReturnProfile)}
          sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', px:2, py:1.2, cursor:'pointer',
            '&:hover':{ bgcolor:'rgba(0,0,0,.015)' }, transition:'background .15s' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <TrendingUpIcon sx={{ fontSize:18, color:'#00897b' }} />
            <Typography sx={{ fontSize:'0.85rem', fontWeight:700, color:'#1e293b' }}>{t('trendPredictions.returnProfile.title')}</Typography>
            <Typography sx={{ fontSize:'0.68rem', color:'#999' }}>{t('trendPredictions.returnProfile.subtitle')}</Typography>
          </Box>
          {showReturnProfile ? <CollapseIcon sx={{ fontSize:20, color:'#999' }} /> : <ExpandIcon sx={{ fontSize:20, color:'#999' }} />}
        </Box>

        {showReturnProfile && returnProfile && (
          <Box sx={{ px:2, pb:2, borderTop:'1px solid #f0f0f0' }}>
            <Typography sx={{ fontSize:'0.68rem', color:'#999', mt:1, mb:1.5 }}>
              {t('trendPredictions.returnProfile.basedOn', { n: returnProfile.last_n_dates })}
            </Typography>

            {/* Verdict return cards */}
            {['STRONG_CONTINUE', 'CONTINUE', 'WEAKENING', 'REVERSAL'].map(v => {
              const vp = returnProfile.by_verdict?.[v];
              if (!vp) return null;
              const vCfg = V[v] || { c:'#666', bg:'#f5f5f5' };
              return (
                <Box key={v} sx={{ mb:1.5, p:1.5, borderRadius:2, border:`1px solid ${rgba(vCfg.c, .15)}`, bgcolor: rgba(vCfg.c, .02) }}>
                  <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1 }}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:.8 }}>
                      <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor: vCfg.dot || vCfg.c }} />
                      <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color: vCfg.c }}>{t(`trendPredictions.verdict.${v}`)}</Typography>
                      <Typography sx={{ fontSize:'0.68rem', color:'#999' }}>{t('trendPredictions.returnProfile.samples', { n: vp.n })}</Typography>
                    </Box>
                    {vp.risk_reward && (
                      <Tooltip title={t('trendPredictions.returnProfile.rrTooltip')} arrow>
                        <Chip size="small" label={`R/R ${vp.risk_reward.toFixed(2)}`}
                          sx={{ height:20, fontSize:'0.62rem', fontWeight:700,
                            bgcolor: vp.risk_reward >= 1.2 ? '#e8f5e9' : vp.risk_reward >= 1.0 ? '#fff8e1' : '#ffebee',
                            color: vp.risk_reward >= 1.2 ? '#2e7d32' : vp.risk_reward >= 1.0 ? '#f57f17' : '#c62828' }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Horizon table */}
                  <Box sx={{ overflowX:'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['horizon', 'pUp', 'expected', 'median', 'worst', 'best', 'range50'].map(h => (
                            <TableCell key={h} sx={{ fontWeight:700, fontSize:'0.6rem', color:'#999', py:.4,
                              textTransform:'uppercase', letterSpacing:.3, whiteSpace:'nowrap' }}>{t(`trendPredictions.returnProfile.horizonHeaders.${h}`)}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {['5', '10', '15', '20'].map(h => {
                          const d = vp.horizons?.[h];
                          if (!d) return null;
                          return (
                            <TableRow key={h} sx={{ '&:last-child td':{ border:0 } }}>
                              <TableCell sx={{ fontSize:'0.75rem', py:.4, fontWeight:600, color:'#555' }}>{t('trendPredictions.returnProfile.nSessions', { n: h })}</TableCell>
                              <TableCell sx={{ py:.4 }}>
                                <Box sx={{ display:'flex', alignItems:'center', gap:.5 }}>
                                  <Box sx={{ width:32, height:5, borderRadius:3, bgcolor:'#eee', overflow:'hidden' }}>
                                    <Box sx={{ width:`${d.p_up||0}%`, height:'100%', borderRadius:3,
                                      bgcolor: d.p_up >= 50 ? '#4caf50' : d.p_up >= 40 ? '#ff9800' : '#ef5350' }} />
                                  </Box>
                                  <Typography sx={{ fontSize:'0.72rem', fontWeight:700,
                                    color: d.p_up >= 50 ? '#2e7d32' : d.p_up >= 40 ? '#f57f17' : '#c62828' }}>
                                    {d.p_up?.toFixed(1)}%
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ fontSize:'0.75rem', py:.4, fontWeight:600,
                                color: (d.avg_ret||0) >= 0 ? '#2e7d32' : '#c62828' }}>
                                {d.avg_ret != null ? `${d.avg_ret >= 0 ? '+' : ''}${d.avg_ret.toFixed(2)}%` : '—'}
                              </TableCell>
                              <TableCell sx={{ fontSize:'0.75rem', py:.4,
                                color: (d.med_ret||0) >= 0 ? '#2e7d32' : '#c62828' }}>
                                {d.med_ret != null ? `${d.med_ret >= 0 ? '+' : ''}${d.med_ret.toFixed(2)}%` : '—'}
                              </TableCell>
                              <TableCell sx={{ fontSize:'0.72rem', py:.4, color:'#c62828' }}>
                                {d.p10 != null ? `${d.p10.toFixed(1)}%` : '—'}
                              </TableCell>
                              <TableCell sx={{ fontSize:'0.72rem', py:.4, color:'#2e7d32' }}>
                                {d.p90 != null ? `+${d.p90.toFixed(1)}%` : '—'}
                              </TableCell>
                              <TableCell sx={{ fontSize:'0.72rem', py:.4, color:'#555' }}>
                                {d.p25 != null && d.p75 != null ? `${d.p25.toFixed(1)}% ~ +${d.p75.toFixed(1)}%` : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>

                  {/* Gain/DD summary */}
                  <Box sx={{ display:'flex', gap:2, mt:.8, px:.5 }}>
                    <Typography sx={{ fontSize:'0.65rem', color:'#999' }}>
                      {t('trendPredictions.returnProfile.maxGainAvg')} <span style={{ color:'#2e7d32', fontWeight:700 }}>+{vp.max_gain_avg?.toFixed(1)}%</span>
                      {' · '}{t('trendPredictions.returnProfile.maxGainMed')} <span style={{ color:'#2e7d32', fontWeight:600 }}>+{vp.max_gain_med?.toFixed(1)}%</span>
                    </Typography>
                    <Typography sx={{ fontSize:'0.65rem', color:'#999' }}>
                      {t('trendPredictions.returnProfile.maxDDAvg')} <span style={{ color:'#c62828', fontWeight:700 }}>{vp.max_dd_avg?.toFixed(1)}%</span>
                      {' · '}{t('trendPredictions.returnProfile.maxDDMed')} <span style={{ color:'#c62828', fontWeight:600 }}>{vp.max_dd_med?.toFixed(1)}%</span>
                    </Typography>
                  </Box>
                </Box>
              );
            })}

            {/* Score bucket summary */}
            {returnProfile.by_score_bucket && (
              <Box sx={{ mt:2 }}>
                <Divider sx={{ mb:1.5 }} />
                <Typography sx={{ fontSize:'0.78rem', fontWeight:700, color:'#1e293b', mb:1 }}>
                  {t('trendPredictions.returnProfile.byScoreTitle')}
                </Typography>
                <Box sx={{ overflowX:'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['score', 'n', 'pUp', 'avgReturn', 'median', 'range', 'maxGain', 'maxDD', 'rr'].map(h => (
                          <TableCell key={h} sx={{ fontWeight:700, fontSize:'0.6rem', color:'#999', py:.4,
                            textTransform:'uppercase', letterSpacing:.3, whiteSpace:'nowrap' }}>{t(`trendPredictions.returnProfile.scoreHeaders.${h}`)}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {['85+', '75-85', '65-75', '55-65', '40-55', '<40'].map(bucket => {
                        const b = returnProfile.by_score_bucket[bucket];
                        if (!b) return null;
                        const isHigh = bucket === '85+' || bucket === '75-85';
                        return (
                          <TableRow key={bucket} sx={{ bgcolor: isHigh ? 'rgba(46,125,50,.02)' : 'transparent', '&:last-child td':{ border:0 } }}>
                            <TableCell sx={{ fontSize:'0.75rem', py:.5, fontWeight:700, color: isHigh ? '#2e7d32' : '#555' }}>
                              {bucket}
                            </TableCell>
                            <TableCell sx={{ fontSize:'0.72rem', py:.5, color:'#888' }}>{b.n}</TableCell>
                            <TableCell sx={{ py:.5 }}>
                              <Box sx={{ display:'flex', alignItems:'center', gap:.5 }}>
                                <Box sx={{ width:28, height:5, borderRadius:3, bgcolor:'#eee', overflow:'hidden' }}>
                                  <Box sx={{ width:`${b.p_up_10d||0}%`, height:'100%', borderRadius:3,
                                    bgcolor: b.p_up_10d >= 50 ? '#4caf50' : b.p_up_10d >= 40 ? '#ff9800' : '#ef5350' }} />
                                </Box>
                                <Typography sx={{ fontSize:'0.72rem', fontWeight:700,
                                  color: b.p_up_10d >= 50 ? '#2e7d32' : b.p_up_10d >= 40 ? '#f57f17' : '#c62828' }}>
                                  {b.p_up_10d?.toFixed(1)}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize:'0.75rem', py:.5, fontWeight:600,
                              color: (b.avg_ret_10d||0) >= 0 ? '#2e7d32' : '#c62828' }}>
                              {b.avg_ret_10d != null ? `${b.avg_ret_10d >= 0 ? '+' : ''}${b.avg_ret_10d.toFixed(2)}%` : '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize:'0.75rem', py:.5,
                              color: (b.med_ret_10d||0) >= 0 ? '#2e7d32' : '#c62828' }}>
                              {b.med_ret_10d != null ? `${b.med_ret_10d >= 0 ? '+' : ''}${b.med_ret_10d.toFixed(2)}%` : '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize:'0.72rem', py:.5, color:'#555' }}>
                              {b.p25_ret_10d != null && b.p75_ret_10d != null
                                ? `${b.p25_ret_10d.toFixed(1)}% ~ +${b.p75_ret_10d.toFixed(1)}%`
                                : '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize:'0.72rem', py:.5, color:'#2e7d32', fontWeight:600 }}>
                              +{b.max_gain_avg?.toFixed(1)}%
                            </TableCell>
                            <TableCell sx={{ fontSize:'0.72rem', py:.5, color:'#c62828', fontWeight:600 }}>
                              {b.max_dd_avg?.toFixed(1)}%
                            </TableCell>
                            <TableCell sx={{ py:.5 }}>
                              <Chip size="small" label={b.risk_reward?.toFixed(2)}
                                sx={{ height:18, fontSize:'0.6rem', fontWeight:700,
                                  bgcolor: b.risk_reward >= 1.2 ? '#e8f5e9' : b.risk_reward >= 1.0 ? '#fff8e1' : '#ffebee',
                                  color: b.risk_reward >= 1.2 ? '#2e7d32' : b.risk_reward >= 1.0 ? '#f57f17' : '#c62828' }} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <Box sx={{ mt:1, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <Tooltip title={t('trendPredictions.footer.formulaTip')} arrow>
          <Typography sx={{ fontSize:'0.65rem', color:'#bbb', cursor:'help', borderBottom:'1px dashed #ddd' }}>
            {t('trendPredictions.footer.formula')}
          </Typography>
        </Tooltip>
      </Box>

      {/* ── History Dialog ── */}
      <HistoryDialog symbol={histSym} open={!!histSym} onClose={() => setHistSym(null)} />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ borderRadius:2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TrendPredictions;
